import { FC } from 'react';
import Grid from '@mui/material/Grid2';
import { Field, useFormikContext } from 'formik';
import { Input } from '@components/input';
import { CreateProjectForm } from '@pages/create-project/create-project';
import { CalculationRequestData } from '@api/projects/mutations';
import { UseMutateFunction } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { SectionWrapper } from "@components/ui-kit/SectionWrapper";
import { MenuItem } from "@mui/material";

interface SecondStepProps {
  calculateBuildingPerformance: UseMutateFunction<any, Error, CalculationRequestData, unknown>;
}

export const FinancialStep: FC<SecondStepProps> = ({calculateBuildingPerformance}) => {
  const { t } = useTranslation();
  const {errors, touched } = useFormikContext<CreateProjectForm>();

  const subsidyRiskTakenOptions = [
    {label: 'EV', value: 'EV'},
    {label: 'Customer', value: 'Customer'},
  ];

  const billingTypeOptions = [
    {label: 'Einzelabrechnung', value: 'Einzelabrechnung'},
    {label: 'Gesamtabrechnung', value: 'Gesamtabrechnung'},
  ];

  const externalSupportOptions = [
    {label: 'Ja', value: 'Ja'},
    {label: 'Nein', value: 'Nein'},
  ];

  const externalManagementOptions = [
    {label: 'Ja', value: 'Ja'},
    {label: 'Nein', value: 'Nein'},
  ];

  return (
    <div className="flex flex-col gap-4">
      <Grid container spacing={2}>
        <Grid size={6}>
          <SectionWrapper title={t('project.initialPriceBlockTitle')} isExpertMode>
              <Grid size={12}>
                <Field
                  as={Input}
                  label={t('project.price_w_ww')}
                  name="price_w_ww"
                  type="number"
                  errors={errors}
                  touched={touched}
                  adornment="EUR/m2*a"
                  fullWidth
                />
              </Grid>

              <Grid size={12}>
                <Field
                  as={Input}
                  label={t('project.price_temperature_conditioning')}
                  name="price_temperature_conditioning"
                  type="number"
                  errors={errors}
                  touched={touched}
                  adornment="EUR/m2*a"
                  fullWidth
                />
              </Grid>

              <Grid size={12}>
                <Field
                  as={Input}
                  label={t('project.ratio_gp_w_ww_vs_gp_temp')}
                  name="ratio_gp_w_ww_vs_gp_temp"
                  type="number"
                  errors={errors}
                  touched={touched}
                  fullWidth
                />
              </Grid>
          </SectionWrapper>
        </Grid>

        <Grid size={6}>
          <SectionWrapper title={t('project.financialParametersBlockTitle')} isExpertMode>
            <Grid size={12}>
              <Field
                as={Input}
                label={t('project.dscr')}
                name="dscr"
                type="number"
                errors={errors}
                touched={touched}
                adornment="%"
                fullWidth
              />
            </Grid>

            <Grid size={12}>
              <Field
                as={Input}
                label={t('project.effective_interest_rate')}
                name="effective_interest_rate"
                type="number"
                errors={errors}
                touched={touched}
                adornment="%"
                fullWidth
              />
            </Grid>

            <Grid size={12}>
              <Field
                as={Input}
                label={t('project.loan_term')}
                name="loan_term"
                type="number"
                errors={errors}
                touched={touched}
                adornment="%"
                fullWidth
              />
            </Grid>
          </SectionWrapper>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid size={6}>
          <SectionWrapper title={t('project.subsidiesBlockTitle')} isExpertMode>
            <Grid size={12}>
              <Field
                as={Input}
                select
                label={t('project.subsidy_risk_taken_by')}
                name="subsidy_risk_taken_by"
                errors={errors}
                touched={touched}
                fullWidth
              >
                {subsidyRiskTakenOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.label)}
                  </MenuItem>
                ))}
              </Field>
            </Grid>

            <Grid size={12}>
              <Field
                as={Input}
                label={t('project.assumption_on_subsidy_rate')}
                name="assumption_on_subsidy_rate"
                type="number"
                errors={errors}
                touched={touched}
                adornment="%"
                fullWidth
              />
            </Grid>

            <Grid size={12}>
              <Field
                as={Input}
                label={t('project.assumption_on_subsidy_payment_date')}
                name="assumption_on_subsidy_payment_date"
                type="number"
                errors={errors}
                touched={touched}
                adornment="Years"
                fullWidth
              />
            </Grid>
          </SectionWrapper>
        </Grid>

        <Grid size={6}>
          <SectionWrapper title={t('project.operationalParametersBlockTitle')} isExpertMode>
            <Grid size={12}>
              <Field
                as={Input}
                select
                label={t('project.billing_type')}
                name="billing_type"
                errors={errors}
                touched={touched}
                fullWidth
              >
                {billingTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.label)}
                  </MenuItem>
                ))}
              </Field>
            </Grid>

            <Grid size={12}>
              <Field
                as={Input}
                select
                label={t('project.external_billing_support')}
                name="external_billing_support"
                errors={errors}
                touched={touched}
                fullWidth
              >
                {externalSupportOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.label)}
                  </MenuItem>
                ))}
              </Field>
            </Grid>

            <Grid size={12}>
              <Field
                as={Input}
                select
                label={t('project.external_operations_management')}
                name="external_operations_management"
                errors={errors}
                touched={touched}
                fullWidth
              >
                {externalManagementOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.label)}
                  </MenuItem>
                ))}
              </Field>
            </Grid>
          </SectionWrapper>
        </Grid>
      </Grid>
    </div>
  );
};
